import * as yup from "yup";

import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReact,
	EnhancedDialog,
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatDate, getAddOnsScreenParentName, scrollPanelFieldsToTop, useNavigateToLogout } from "../../../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AccountSettingsService } from "../../../../services/AddOns/Accounting/AccountSettingsService";
import { BasicSetupFormToolbar } from "../../../BasicSetupFormToolbar";
import { BasicTwoColumnLayoutShowOnlyTitleSetupToolbar } from "../../../BasicTwoColumnLayoutShowOnlyTitleSetupToolbar";
import { CRACreditService } from "../../../../services/CRACreditService";
import { EmployeeService } from "../../../../services/EmployeeService";
import { ExportDataService } from "../../../../services/AddOns/Accounting/ExportDataService";
import { ExportDataSetupToolbar } from "./ExportDataSetupToolbar";
import { IAppState } from "../../../../store/reducers";
import { IDropDownListResponse } from "../../../../models/common/IDropDownListResponse";
import { IExportData } from "../../../../models/AddOns/Accounting/ExportData";
import { IGetListRequest } from "../../../../models";
import { InvoiceService } from "../../../../services/InvoiceService";
import { NoFoundPage } from "../../../../pages/Anonymous/NoFoundPage";
import { PaydayAdvanceInfoSetupFieldsPanel } from "./Panels/PaydayAdvanceInfoSetupFieldsPanel";
import { PayrollInfoSetupFieldsPanel } from "./Panels/PayrollInfoSetupFieldsPanel";
import { SimplePayInvoiceInfoSetupFieldsPanel } from "./Panels/SimplePayInvoiceInfoSetupFieldsPanel";
import { ZohoBooksService } from "../../../../services/AddOns/Accounting/ZohoBooks/ZohoBooksService";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../../store/actions";
import { sortDDLByLabel } from "../../../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";
import { YearService } from "../../../../services/YearService";
import { TaxSlipsInfoSetupFieldsPanel } from "./Panels/TaxSlipsInfoSetupFieldsPanel";
import { VendorPaymentInfoSetupFieldsPanel } from "./Panels/VendorPaymentInfoSetupFieldsPanel";

// import { ExportDataValidationSchema } from "./validator";













export interface IExportDataSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "single-data-record";
const CONNECT_ZOHO_PERMISSION_DATA_QUERY_KEY = "connect-zoho-permission-record";

const useGetConnectionPermission = (
	exportDataPageName: string,
	clientId: string,
) =>
	useQuery({
		queryKey: [CONNECT_ZOHO_PERMISSION_DATA_QUERY_KEY, exportDataPageName, clientId],
		queryFn: () => {
			return AccountSettingsService.getAccountSettingsDDL(exportDataPageName, clientId);
		},
		refetchOnWindowFocus: false,
	});

// const useGetData = (
// 	clientId: string,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, navigateToLogout],
// 		queryFn: () => {
// 			return ZohoBooksService.getExportData(clientId, navigateToLogout);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayPeriodEndDateDDL = (
	clientId: string,
	employeePayPeriodId: string | null,
	taxYear: number
) =>
	useQuery({
		queryKey: [
			"periods-ddl",
			employeePayPeriodId,
		],
		queryFn: () => {
			return CRACreditService.getPayPeriodDDL(
				clientId,
				employeePayPeriodId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetInvoiceDDL = (clientId: string) =>
	useQuery({
		queryKey: ["invoice-ddl"],
		queryFn: (data) => {
			return InvoiceService.getDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["Employee-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useYearDDL = () =>
	useQuery({
		queryKey: ["tax-tear-ddl"],
		queryFn: () => {
			return YearService.getTaxYearDDL();
		},
		refetchOnWindowFocus: false,
	});

export const ExportData: React.FC<IExportDataSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const navigateToLogout = useNavigateToLogout();

	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const [selectedPeriodEndDate, setSelectedPeriodEndDate] = useState<string | null>(null);
	const [exportDataCurrentPanelTag, setExportDataCurrentPanelTag] = useState<string | null>(null);
	const exportDataCurrentPanelTagRef = useRef<string | null>(null);

	const setActivePanelTag = (tag: string) => {
		exportDataCurrentPanelTagRef.current = tag; // synchronous, always current
		setExportDataCurrentPanelTag(tag);          // for re-render/UI only
	};

	const { id, ExportDataPageName } = useParams();

	// exportDataPageName would be like 
	// net_suite
	// zoho_books
	// xero
	// quick_books

	const [exportDataPageName, setExportDataPageName] = useState<string>("");
	useEffect(() => {
		if (ExportDataPageName && ExportDataPageName.length > 0) {
			setExportDataPageName(ExportDataPageName);
			if (ExportDataPageName !== exportDataPageName) {
				formMethods.reset();
			}
		}
	}, [exportDataPageName, ExportDataPageName]);

	// const ExportDataValidationSchema = yup.object().shape({
	// 	paymentDate: yup
	// 		.string()
	// 		.nullable()
	// 		.when("$exportDataCurrentPanelTag", {
	// 			is: (tag: string | null) => tag === "payroll",
	// 			then: (schema) => schema.required("Payment Date is required"),
	// 			otherwise: (schema) => schema.nullable(),
	// 		}),
	// });

	const payrollRequiredFields = ["paymentDate"];
	const paydayAdvanceRequiredFields = ["exportDate", "referenceNo"];
	const simplePayInvoiceRequiredFields = ["invoiceId", "dueDate"];

	const PayrollValidationSchema = yup.object().shape({
		paymentDate: yup
			.string()
			.required("Transaction Date is required"),
	});
	const PaydayAdvanceValidationSchema = yup.object().shape({
		exportDate: yup
			.string()
			.required("Export Date is required"),
		referenceNo: yup
			.string()
			.required("Reference No is required"),
	});
	const SimplePayInvoiceValidationSchema = yup.object().shape({
		invoiceId: yup
			.string()
			.required("Invoice No is required"),
		dueDate: yup
			.string()
			.required("Due Date is required"),
	});

	const schemasByTag: Record<string, yup.ObjectSchema<any>> = {
		payroll: PayrollValidationSchema,
		payday_advance: PaydayAdvanceValidationSchema,
		simplepay_invoice: SimplePayInvoiceValidationSchema,
	};

	const fieldsByTag: Record<string, string[]> = {
		payroll: payrollRequiredFields,
		payday_advance: paydayAdvanceRequiredFields,
		simplepay_invoice: simplePayInvoiceRequiredFields,
	};


	const formMethods = useForm<any>({
		resolver: async (values, context, options) => {
			const tag = exportDataCurrentPanelTagRef.current;
			const schema = tag ? schemasByTag[tag] : yup.object().shape({});
			return yupResolver(schema)(values, context, options);
		},
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	// const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
	// 	clientId,
	// 	navigateToLogout
	// );
	const { isPending: isPendingConnectionPermision, data: accountSettingsDDL } = useGetConnectionPermission(
		exportDataPageName,
		clientId
	);

	// useEffect(() => {
	// 	if (accountSettingsDDL && accountSettingsDDL.status === 400 && exportDataPageName === 'xero') {
	// 		navigate(`/add-ons/account-settings/xero`)
	// 	} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && exportDataPageName === 'zoho_books') {
	// 		navigate(`/add-ons/zoho-books/connect-zoho`)
	// 	} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && exportDataPageName === 'net_suite') {
	// 		navigate(`/add-ons/net-suite/setup`)
	// 	} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && exportDataPageName === 'quick_books') {
	// 		navigate(`/add-ons/account-settings/quick_books`)
	// 	} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && exportDataPageName === 'kashoo') {
	// 		navigate(`/add-ons/account-settings/kashoo`)
	// 	}
	// }, [accountSettingsDDL])
	const { isLoading: isLoadingPayrPeriodFrequencyDDL, data: payPeriodFrequnecyDDL } = useGetPayFrequencyDDL(clientId, taxYear);
	const { isLoading: isLoadingPayPeriodEndDateDDL, data: payPeriodEndDateDDL } = useGetPayPeriodEndDateDDL(clientId, selectedPeriodEndDate, taxYear);
	const { isLoading: isLoadingInvoiceDDL, data: invoiceDDL } = useGetInvoiceDDL(clientId);
	const { isLoading: isLoadingemployeeDDL, data: employeeDDL } = useGetEmployeeDDL(clientId);
	const { isPending: isPendingTaxYearDDL, data: taxYearDDL } = useYearDDL();


	const payPeriodEndDateDDLItems = useMemo(() => {
		if (payPeriodEndDateDDL) {
			if (
				payPeriodEndDateDDL &&
				Array.isArray(payPeriodEndDateDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodEndDateDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [{
				label: "All",
				value: "all",
			}];
		}
		return [{
			label: "All",
			value: "all",
		}];
	}, [payPeriodEndDateDDL]);


	const payPeriodFrequencyDDLItems = useMemo(() => {
		if (payPeriodFrequnecyDDL) {
			if (
				payPeriodFrequnecyDDL &&
				Array.isArray(payPeriodFrequnecyDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodFrequnecyDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payPeriodFrequnecyDDL]);


	const { isPending: isSubmitting, mutate: submitExportDataFormFields } =
		useMutation({
			mutationFn: ExportDataService.createExportData,
			onSuccess: (res: IExportData | IServerError | IResponse | any) => {

				if (res.status === 200) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
					let path = `/add-ons/export-data/${exportDataPageName}`;
					navigate(path);
					return Promise.all([

						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});

					scrollPanelFieldsToTop();
					let path = `/add-ons/export-data/${exportDataPageName}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});


	const onSubmit = (values: any) => {
		// alert(exportDataCurrentPanelTag)
		// values.exportDataCurrentPanelTag is guaranteed fresh — it came from the click, not state
		const payload = {
			clientId: clientId,
			id: id && id,
			taxYear: taxYear && taxYear,
			navigateToLogout,
			exportDataCurrentPanelTag: values.exportDataCurrentPanelTag,
			exportDataPageName: exportDataPageName,
			...(values.exportDataCurrentPanelTag === "payroll" && {
				payrollSheduleId: values.payrollSheduleId,
				payPeriodEndDate: values.payPeriodEndDate,
				startDate: formatDate(values.startDate),
				endDate: formatDate(values.endDate),
				paymentDate: formatDate(values.paymentDate),
				exportRecordAs: values.exportRecordAs,
				exportPayroll: values.exportPayroll,
				exportRqRemittance: values.exportRqRemittance,
				exportCraRemittance: values.exportCraRemittance,
				exportEhtRemittance: values.exportEhtRemittance,
				exportWsibRemittance: values.exportWsibRemittance,
				includeNonCash: values.includeNonCash,
				trackingCategories: values.trackingCategories,
				glReference: exportDataPageName === 'quick_books' ? values.glReference : undefined,
				memo: exportDataPageName === 'quick_books' ? values.memo : undefined,
				exportAs: exportDataPageName === 'quick_books' ? values.exportAs : undefined,
				accountDescriptionInDescriptionField: exportDataPageName === 'quick_books' ? values.accountDescriptionInDescriptionField : undefined,
				employeeCodeInInDescriptionField: exportDataPageName === 'quick_books' ? values.employeeCodeInInDescriptionField : undefined,
				employeeNameInDescriptionField: exportDataPageName === 'quick_books' ? values.employeeNameInDescriptionField : undefined,
				chequeNoInDescriptionFielWithpayrollClearIngentry: exportDataPageName === 'quick_books' ? values.chequeNoInDescriptionFielWithpayrollClearIngentry : undefined,
			}),
			...(values.exportDataCurrentPanelTag === "payday_advance" && {
				employeeId: values.employeeId,
				stDate: formatDate(values.stDate),
				enDate: formatDate(values.enDate),
				exportDate: formatDate(values.exportDate),
				referenceNo: values.referenceNo,
				glReference: exportDataPageName === 'quick_books' ? values.glReference : undefined,
				memo: exportDataPageName === 'quick_books' ? values.memo : undefined,
			}),
			...(values.exportDataCurrentPanelTag === "simplepay_invoice" && {
				invoiceId: values.invoiceId,
				dueDate: formatDate(values.dueDate),
				glReference: exportDataPageName === 'quick_books' ? values.glReference : undefined,
				memo: exportDataPageName === 'quick_books' ? values.memo : undefined,
			}),
			...(values.exportDataCurrentPanelTag === "vendor_payments" && {
				stDate: formatDate(values.stDate),
				enDate: formatDate(values.enDate),
				dueDate: formatDate(values.dueDate),
				checkNoInMemo: values.checkNoInMemo,
				AccountDescriptionInDescriptionField: values.AccountDescriptionInDescriptionField,
				notesInDescriptionFieldWithPaymentEntry: values.notesInDescriptionFieldWithPaymentEntry,
			}),
		};

		
		submitExportDataFormFields(payload);
	};

	const ImportButtonClickedHandler = async (tag: string) => {
		formMethods.clearErrors();
		setActivePanelTag(tag); // updates ref synchronously + state for UI

		const fieldsToValidate = fieldsByTag[tag] ?? [];
		const isValid = await formMethods.trigger(fieldsToValidate);

		if (!isValid) return; // errors will show inline, scoped to that panel's fields only

		const values = formMethods.getValues();
		onSubmit({ ...values, exportDataCurrentPanelTag: tag }); // tag passed explicitly, not from state
	};

	const ActivateTaxSlipsAddOnClickedHandler = async () => {
		alert("coming soon.....")
	};

	// useEffect(() => {
	// 	if (clientId && selectedDataRecord) {
	// 		Object.keys(selectedDataRecord).forEach((fieldName) => {
	// 			const fieldValue = selectedDataRecord[fieldName];
	// 			formMethods.setValue(fieldName, fieldValue ?? undefined);
	// 			if (fieldName === "clientId") {
	// 				let path = `/add-ons/zoho-books/export-data${clientId}`
	// 				navigate(path)
	// 			}
	// 		});
	// 	}
	// }, [clientId, selectedDataRecord]);


	const expandedLabels = ["Add-Ons", `${getAddOnsScreenParentName(exportDataPageName)}`];
	const expandedAddOnsOrListItem = "Export Data";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === `${getAddOnsScreenParentName(exportDataPageName)}` && item.label === expandedAddOnsOrListItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === `${getAddOnsScreenParentName(exportDataPageName)}` && parentLabel === `${getAddOnsScreenParentName(exportDataPageName)}` && item.label === expandedAddOnsOrListItem && `/add-ons/export-data/${ExportDataPageName}`
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	let acceptablExportDataPages = ["net_suite", "zoho_books", "xero", "quick_books", "kashoo", "taxslips"]

	return (
		<>{ExportDataPageName && acceptablExportDataPages.includes(ExportDataPageName) ? <Grid>
			<BackdropCircularProgress
				open={isSubmitting || isLoadingPayrPeriodFrequencyDDL || isPendingConnectionPermision || isLoadingInvoiceDDL || isLoadingPayPeriodEndDateDDL || isLoadingemployeeDDL || isPendingTaxYearDDL}
				testId="export-data"
			/>
			{/* {accountSettingsDDL && accountSettingsDDL.expenseaccounts && */}
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
							<TwoColumnLayout
								leftColumnContent={
									<>
										{exportDataPageName === 'taxslips' ? <StandardPanel
											title="taxslips"
											testId={`taxslips`}
											borderHide={true}
											showTitle={false}
										>
											<EnhancedDropDownRHFWithLabel
												selectName={"selectYear"}
												items={taxYearDDL ?? []}
												// selectedItem={"all"}
												label={"Select Year"}
												testId={"select-year-ddl"}
												showEmpty
											/>

										</StandardPanel> : <></>}

										{exportDataPageName === 'taxslips' ? <StandardPanel
											title="Activate TaxSlips Add On"
											showTitle={false}
											testId={``}
											borderHide={true}>
											<TaxSlipsInfoSetupFieldsPanel
												formMethods={formMethods}
												ActivateTaxSlipsAddOnClickedHandler={ActivateTaxSlipsAddOnClickedHandler}
											/>
										</StandardPanel> : <></>}

										{exportDataPageName === 'xero' || exportDataPageName === 'quick_books' ? <StandardPanel
											title="Payroll"
											testId={`payroll`}
											borderHide={true}
										>
											<PayrollInfoSetupFieldsPanel
												formMethods={formMethods}
												payPeriodFrequencyDDLItems={payPeriodFrequencyDDLItems}
												payPeriodEndDateDDL={payPeriodEndDateDDLItems}
												onPeriodEndDateSelectionChangeHandler={(value: string) => setSelectedPeriodEndDate(value)}
												isExporting={false}
												handleImportClick={ImportButtonClickedHandler}
												exportDataPageName={exportDataPageName}
											/>

										</StandardPanel> : <></>}
										{exportDataPageName === 'xero' || exportDataPageName === 'quick_books' ? <StandardPanel
											title="SimplePay Invoice"
											testId={`simplepay-info`}
											borderTop={true}
											borderHide={true}
											variant="outlined"
											topPadding={5}
										>
											<SimplePayInvoiceInfoSetupFieldsPanel
												invoiceDDL={invoiceDDL && sortDDLByLabel(invoiceDDL)}
												isExporting={false}
												handleImportClick={ImportButtonClickedHandler}
												exportDataPageName={exportDataPageName}
											/>
										</StandardPanel> : <></>}
									</>
								}
								rightColumnContent={
									<>
										{exportDataPageName === 'quick_books' ? <StandardPanel
											title="Vendor Payments"
											testId={`vendor-payments`}
											borderHide={true}
											borderBottom={true}>
											<VendorPaymentInfoSetupFieldsPanel
												employeeDDL={employeeDDL && sortDDLByLabel(employeeDDL)}
												isExporting={false}
												handleImportClick={ImportButtonClickedHandler}
												formMethods={formMethods}
												exportDataPageName={exportDataPageName}
											/>
										</StandardPanel> : <></>}

										{exportDataPageName === 'taxslips' ? <StandardPanel
											title="taxslips"
											testId={`taxslips`}
											borderHide={true}
											showTitle={false}>
											<div
												style={{
													display: "flex",
													justifyContent: "end",
													alignItems: "start",
													height: "40px",
													width: "100%",
													marginTop: "1.5rem"
												}}
											>
												<img
													src={"https://www.simplepay.ca/canada_payroll//resources/images/taxslips_logo.jpg"}
													alt="loading"
													style={{
														maxWidth: "180px",
														maxHeight: "100%",
														objectFit: "contain",
														cursor: "pointer",
													}}
												/>

											</div>
										</StandardPanel> : <></>}

										{exportDataPageName === 'xero' || exportDataPageName === 'quick_books' ? <StandardPanel
											title="Payday Advance"
											testId={`payday-advance`}
											borderHide={true}>
											<PaydayAdvanceInfoSetupFieldsPanel
												employeeDDL={employeeDDL && sortDDLByLabel(employeeDDL)}
												isExporting={false}
												handleImportClick={ImportButtonClickedHandler}
												formMethods={formMethods}
												exportDataPageName={exportDataPageName}
											/>
										</StandardPanel> : <></>}
									</>
								}
								toolbar={
									<BasicTwoColumnLayoutShowOnlyTitleSetupToolbar
										pageTitle={"Export Data"}
									/>
								}
								testId="form-layout"
							/>
						</Grid>

						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>
			{/* } */}

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"export-data-snackbar"}
				open={snackbarData.open}
			/>
		</Grid> : <NoFoundPage />}</>
	);
};
